import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users2, Copy, LogOut, Sparkles } from 'lucide-react';
import GroupChat from '@/components/GroupChat';
import MilestoneNFT from '@/components/MilestoneNFT';

export default function ReferralGroups() {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/referral_groups/mine');
      setGroup(r.data.group);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error('Give your group a name');
    setCreating(true);
    try {
      await api.post('/referral_groups', { name: name.trim() });
      toast.success('Group created — share the invite code');
      setName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setCreating(false);
  };

  const join = async () => {
    if (!inviteCode.trim()) return toast.error('Enter an invite code');
    setJoining(true);
    try {
      await api.post('/referral_groups/join', { invite_code: inviteCode.trim().toUpperCase() });
      toast.success('Joined group');
      setInviteCode('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setJoining(false);
  };

  const leave = async () => {
    if (!window.confirm('Leave this group? You will stop receiving splits.')) return;
    try {
      await api.post('/referral_groups/leave');
      toast.success('Left the group');
      setGroup(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return null;

  return (
    <div className="mt-10 mb-10" data-testid="referral-groups-section">
      <div className="flex items-center gap-2 mb-3">
        <Users2 className="w-5 h-5 text-[#FCD535]" />
        <h2 className="text-white text-xl font-semibold">Referral Groups</h2>
        <span className="text-xs uppercase tracking-wider text-[#848E9C]">team up, split evenly</span>
      </div>

      {!group ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[#2B3139] bg-[#181A20] p-5">
            <div className="text-white font-semibold mb-1">Create a group</div>
            <div className="text-xs text-[#B7BDC6] mb-3">Up to 5 friends. All referral bonuses earned by any member are split evenly.</div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Diamond Hands Crew"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mb-3"
              data-testid="group-name-input"
              maxLength={40}
            />
            <Button onClick={create} disabled={creating}
              className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm"
              data-testid="create-group-btn">
              {creating ? 'Creating…' : 'Create Group'}
            </Button>
          </div>
          <div className="border border-[#2B3139] bg-[#181A20] p-5">
            <div className="text-white font-semibold mb-1">Join a group</div>
            <div className="text-xs text-[#B7BDC6] mb-3">Ask a friend for their group invite code.</div>
            <Input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="INVITE CODE"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono uppercase tracking-widest mb-3"
              data-testid="group-invite-input"
              maxLength={12}
            />
            <Button onClick={join} disabled={joining} variant="outline"
              className="w-full border-[#FCD535]/40 bg-transparent text-[#FCD535] hover:bg-[#FCD535]/10 hover:text-[#FCD535] rounded-sm"
              data-testid="join-group-btn">
              {joining ? 'Joining…' : 'Join Group'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-[#FCD535]/30 bg-gradient-to-br from-[#FCD535]/8 to-[#181A20] p-5" data-testid="group-card">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-white font-semibold text-lg">{group.name}</div>
              <div className="text-xs text-[#848E9C] mt-0.5">
                {group.size}/{group.max_size} members · {group.seats_left} seat{group.seats_left === 1 ? '' : 's'} left
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-[#0B0E11] border border-[#2B3139] px-3 py-1.5">
                <span className="text-xs text-[#848E9C] uppercase">Invite:</span>
                <span className="font-mono text-[#FCD535] tracking-widest">{group.invite_code}</span>
                <button onClick={() => { navigator.clipboard.writeText(group.invite_code); toast.success('Copied'); }}
                  data-testid="copy-invite-code" className="text-[#848E9C] hover:text-white">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <Button onClick={leave} variant="outline" size="sm"
                className="border-[#F6465D]/40 bg-transparent text-[#F6465D] hover:bg-[#F6465D]/10 hover:text-[#F6465D] rounded-sm"
                data-testid="leave-group-btn">
                <LogOut className="w-3.5 h-3.5 mr-1.5" /> Leave
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-xs uppercase text-[#848E9C]">Pool Total</div>
              <div className="font-mono text-[#FCD535] text-2xl mt-1">{(group.pool_total_usdt || 0).toFixed(0)}</div>
              <div className="text-[10px] text-[#5E6673]">USDT contributed</div>
            </div>
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-xs uppercase text-[#848E9C]">Splits</div>
              <div className="font-mono text-white text-2xl mt-1">{group.recent_splits?.length || 0}</div>
            </div>
            <div className="col-span-2 sm:col-span-2 border border-[#FCD535]/30 bg-[#FCD535]/8 p-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FCD535]" />
              <div className="text-xs text-[#EAECEF]">
                Every referral bonus earned by a member is split <span className="text-[#FCD535] font-semibold">equally across all members</span>.
              </div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-2">Members</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="group-members">
            {group.members_detail?.map(m => (
              <div key={m.user_id} className="flex items-center gap-2 border border-[#2B3139] bg-[#0B0E11] px-3 py-2">
                {m.picture ? (
                  <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#2B3139] grid place-items-center text-[#EAECEF] text-sm font-semibold">
                    {m.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm truncate">{m.name}</div>
                  <div className="text-[10px] text-[#5E6673] font-mono truncate">{m.email_masked}</div>
                </div>
                {m.is_creator && <span className="text-[9px] font-mono uppercase text-[#FCD535] bg-[#FCD535]/15 px-1.5 py-0.5">Owner</span>}
              </div>
            ))}
          </div>

          {group.recent_splits?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-2">Recent splits</div>
              <div className="border border-[#2B3139] bg-[#0B0E11] max-h-40 overflow-y-auto">
                {group.recent_splits.map(s => (
                  <div key={s.event_id} className="grid grid-cols-12 px-3 py-2 border-b border-[#2B3139]/60 items-center text-xs">
                    <div className="col-span-4 text-[#B7BDC6]">{new Date(s.created_at).toLocaleDateString()}</div>
                    <div className="col-span-4 text-[#EAECEF]">{s.member_count} members</div>
                    <div className="col-span-4 text-right font-mono text-[#0ECB81]">+{s.per_member_share?.toFixed(0)} USDT each</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(group.nfts?.length > 0 || group.nft) && <MilestoneNFT nfts={group.nfts} nft={group.nft} groupName={group.name} />}
          <GroupChat />
        </div>
      )}
    </div>
  );
}
